import { createFileRoute, Link } from "@tanstack/react-router";

export const Route = createFileRoute("/privacy")({
  head: () => {
    const ogImage = "https://plunkets.ie/og-default.jpg";
    return {
      meta: [
        { title: "Privacy Policy — Plunkets Pub Dublin" },
        {
          name: "description",
          content:
            "How Plunkets Pub Dublin handles your data: table reservations, WhatsApp and Instagram messages (including AI-assisted replies), what we store, how long we keep it and how to request deletion.",
        },
        { property: "og:title", content: "Privacy Policy — Plunkets Pub Dublin" },
        {
          property: "og:description",
          content:
            "How we handle your reservations, WhatsApp and Instagram messages, and how to request deletion of your data.",
        },
        { property: "og:type", content: "website" },
        { property: "og:url", content: "https://plunkets.ie/privacy" },
        { property: "og:image", content: ogImage },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:locale", content: "en_IE" },
        { property: "og:site_name", content: "Plunkets Pub Dublin" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: "Privacy Policy — Plunkets Pub Dublin" },
        {
          name: "twitter:description",
          content:
            "How we handle your reservations, WhatsApp and Instagram messages, and how to request deletion of your data.",
        },
        { name: "twitter:image", content: ogImage },
      ],
      links: [{ rel: "canonical", href: "https://plunkets.ie/privacy" }],
    };
  },
  component: PrivacyPage,
});

const EMAIL = "bookings@plunkets.ie";

function H({ children }: { children: string }) {
  return <h2 className="mt-10 text-lg font-semibold text-foreground first:mt-0">{children}</h2>;
}

function PrivacyPage() {
  return (
    <div className="min-h-screen bg-background px-5 py-16 md:px-8">
      <article className="mx-auto max-w-3xl text-sm leading-relaxed text-muted-foreground">
        <p className="text-xs uppercase tracking-[0.2em] text-primary">Legal</p>
        <h1 className="mt-2 text-3xl font-bold tracking-tight text-foreground">
          Privacy Policy
        </h1>
        <p className="mt-2 text-xs text-muted-foreground">
          Last updated: October 2026
        </p>

        <p className="mt-6">
          This policy explains how <strong className="text-foreground">Plunkets Pub</strong>{" "}
          ("we", "us"), Middle Abbey Street, Dublin 1, D01, Ireland, handles your personal
          data when you book a table, message us on WhatsApp or Instagram, or use our website
          (plunkets.ie). For any privacy question or request, contact us at{" "}
          <a href={`mailto:${EMAIL}`} className="text-primary hover:underline">
            {EMAIL}
          </a>
          .
        </p>

        <H>1. Reservations</H>
        <p className="mt-2">
          When you make a table, pool, darts or Karaoke Terrace booking we store: your name,
          phone number, email address (if provided), party size, date, time, booking type,
          preferred area, the booking reference, and any notes you or our staff add. We use
          this to manage your booking, send confirmation and "your table is confirmed" emails,
          and to contact you if plans change. We do not take deposits or store card details
          for standard bookings.
        </p>

        <H>2. WhatsApp and Instagram messages (including AI assistance)</H>
        <p className="mt-2">
          When you message us on WhatsApp or Instagram, we store the messages you send, your
          contact identifier (phone number or Instagram user ID and username), and our
          replies. To answer quickly, some messages are handled with the help of an automated
          AI assistant. The AI uses only information about our pub — opening hours, menus,
          events, live sport and music, pool, darts and Karaoke Terrace details, and booking
          links — to suggest replies. Your messages may also be read by our staff, who can
          take over any conversation at any time.
        </p>
        <p className="mt-2">
          The AI assistant does not create bookings by itself; it shares booking links and
          our email address. Your messages are also processed by the platform providers
          (Meta, for Instagram and WhatsApp) under their own privacy policies.
        </p>

        <H>3. Website usage data</H>
        <p className="mt-2">
          We use Google Analytics to understand how visitors use our site (pages viewed,
          approximate location, device type). This data is aggregated and is not used to
          identify you personally. Our site uses essential cookies for bookings and
          analytics cookies for these measurements; you can block cookies in your browser
          settings.
        </p>

        <H>4. What we do NOT do</H>
        <p className="mt-2">
          We do not sell your personal data. We do not use your data for advertising, and we
          do not share it with third parties except the service providers listed above
          (hosting, email delivery, Meta, Google Analytics) and where the law requires it.
        </p>

        <H>5. How long we keep your data</H>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          <li>Reservations: up to 24 months after your visit, for our records and to prevent duplicate bookings.</li>
          <li>WhatsApp and Instagram messages: up to 12 months.</li>
          <li>Email confirmations: transactional copies are kept for up to 12 months.</li>
          <li>Website analytics: as per Google Analytics' standard retention.</li>
        </ul>
        <p className="mt-2">After these periods, data is deleted or anonymised.</p>

        <H>6. How to request a copy or deletion of your data</H>
        <p className="mt-2">
          You can ask us at any time for a copy of the personal data we hold about you, ask
          us to correct it, or ask us to delete it. Email{" "}
          <a href={`mailto:${EMAIL}`} className="text-primary hover:underline">
            {EMAIL}
          </a>{" "}
          with your request — include your name and phone number (or the Instagram/WhatsApp
          account you used) so we can find your records. We will respond within 30 days.
          Deleting message history in your own WhatsApp or Instagram app does not delete our
          copy, so please contact us directly.
        </p>

        <H>7. Your rights</H>
        <p className="mt-2">
          Under the GDPR you have the right to access, rectify, erase, restrict or object to
          the processing of your personal data, and to data portability. You also have the
          right to complain to the Data Protection Commission (dataprotection.ie) if you are
          unhappy with how we handle your data.
        </p>

        <H>8. Changes to this policy</H>
        <p className="mt-2">
          We may update this policy from time to time. The latest version is always
          available on this page.
        </p>

        <div className="mt-12 border-t border-border pt-6">
          <Link to="/" className="text-primary hover:underline">
            ← Back to Plunkets.ie
          </Link>
        </div>
      </article>
    </div>
  );
}
